import { useEffect, useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { Check, MapPin, Plus } from "lucide-react";

import { Crumbs, DesktopShell } from "../../components/desktop/shell.tsx";
import { Button, Empty, ErrorBlock, Img, Input, Price, Spinner, fenInt } from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { useCheckout } from "../../lib/use-checkout.ts";
import { RegionPicker } from "../../components/region-picker.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 确认订单
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **左边"要确认什么"，右边"要付多少"，右边常驻。**
 *    手机版是地址 → 商品 → 明细 → 券 → 备注一路往下滚，金额条钉在屏幕底部 ——
 *    窄屏上只能这样。网页上一屏能放下两栏：用户改地址、输券码时，
 *    **实付金额一直在视野里**，不用滚上滚下确认自己在付多少。
 *
 * ⚠️ 地址做成**卡片网格**（一行两张）而不是竖列表：
 *    常用地址通常两三个，横着摆一眼看全，不用挨个滚。
 *
 * ⚠️ 逻辑全部来自 `useCheckout()` —— 跟手机版是同一份。
 */
export function DesktopCheckout() {
  /** ⚠️ 跟手机版同一条：地址栏带 skuId 就是「立即下单」。 */
  const { skuId, qty } = useSearch({ from: "/checkout" });
  const c = useCheckout(skuId ? { skuId, quantity: qty && qty > 0 ? qty : 1 } : null);
  const [couponInput, setCouponInput] = useState("");
  const [showForm, setShowForm] = useState(false);
  /** ⚠️ 一动表单就把「还差…」那条提示清掉，见 `use-checkout.ts`。 */
  const clearErr = c.clearAddressError;
  const [form, setForm] = useState({
    receiver: "",
    phone: "",
    province: "",
    city: "",
    district: "",
    detail: "",
  });

  /**
   * 🔴 **这个 effect 必须排在下面那几个 early return 之前。**
   *    React 的 hook 数量每次渲染必须一样多 —— 放在 return 后面的话，
   *    购物车还在查询的那一轮少跑一个，数据回来的那一轮又多出来，
   *    直接抛「Rendered more hooks than during the previous render」整页白屏。
   *    ⚠️ **而且它是概率性的**：查询命中缓存时根本没有 pending 那一轮，
   *    页面就好好的 —— 本地点几次可能都碰不到，冷启动才炸。
   *    （这个坑本项目犯过不止一次，见 MEMORY「hook 必须全部排在 early return 之前」。）
   *
   * ⚠️ 作用：用户一动收货地址表单就把「还差…」那条提示清掉。
   */
  useEffect(() => {
    clearErr();
  }, [form, clearErr]);

  const { cart, addresses } = c;

  if (c.cartQuery.isPending)
    return (
      <DesktopShell>
        <Spinner compact />
      </DesktopShell>
    );
  if (c.cartQuery.error)
    return (
      <DesktopShell>
        <ErrorBlock error={c.cartQuery.error} />
      </DesktopShell>
    );
  if (!cart || cart.groups.length === 0)
    return (
      <DesktopShell>
        <Empty title="购物车是空的" action={{ label: "去逛逛", to: "/products" }} compact />
      </DesktopShell>
    );

  /** 🔴 跟手机版同一条：地址没存就顺手存了，绝不静默无反应。见 `use-checkout.ts`。 */
  const submit = async () => {
    setShowForm(true);
    await c.submit(form);
  };

  return (
    <DesktopShell>
      <Crumbs
        items={[{ label: "首页", to: "/" }, { label: "购物车", to: "/cart" }, { label: "确认订单" }]}
      />

      <div className="grid grid-cols-[minmax(0,1fr)_340px] items-start gap-8">
        {/* ══════════ 左：要确认的东西 ══════════ */}
        <div className="space-y-5">
          {/* ⚠️ 价格变动不是"出错"，是要用户再看一眼 —— 用提示色而不是报错色。 */}
          {c.priceChanged && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-amber-800">
              <p className="text-[15px] font-medium">商品价格有变动，已为你更新</p>
              <p className="mt-0.5 text-[13px]">请确认右侧的金额后再提交。</p>
            </div>
          )}
          {c.createOrder.error && !c.priceChanged && <ErrorBlock error={c.createOrder.error} />}

          {/* ── 收货地址 ── */}
          <section className="rounded-xl border border-border bg-card p-5">
            <div className="mb-3.5 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-[15px] font-medium">
                <MapPin className="size-[18px] text-muted-foreground" />
                收货地址
              </h2>
              {addresses && addresses.length > 0 && !showForm && (
                <button
                  className="flex items-center gap-1 text-[13px] text-brand-600 hover:text-brand-700"
                  onClick={() => setShowForm(true)}
                >
                  <Plus className="size-3.5" />
                  新增地址
                </button>
              )}
            </div>

            {addresses && addresses.length > 0 && !showForm ? (
              /* ⚠️ 一行两张卡：地址通常两三个，横着摆一眼看全。 */
              <div className="grid grid-cols-2 gap-3">
                {addresses.map((a) => {
                  const selected = c.addressId === a.id;
                  return (
                    <button
                      key={a.id}
                      onClick={() => c.setAddressId(a.id)}
                      className={cn(
                        "relative rounded-lg border p-4 text-left transition-colors",
                        selected
                          ? "border-brand-500 bg-brand-50/60"
                          : "border-border hover:border-brand-300",
                      )}
                    >
                      {/* ⚠️ 选中态要有明确的勾 —— 只靠边框变色，色弱用户分不出来。 */}
                      {selected && (
                        <span className="absolute top-3 right-3 flex size-5 items-center justify-center rounded-full bg-brand-600">
                          <Check className="size-3 text-white" strokeWidth={3} />
                        </span>
                      )}
                      <p className="pr-7 text-[14px]">
                        {a.receiver}
                        <span className="ml-2 text-muted-foreground">{a.phone}</span>
                        {a.isDefault && (
                          <span className="ml-2 rounded bg-brand-100 px-1.5 py-0.5 text-[11px] text-brand-700">
                            默认
                          </span>
                        )}
                      </p>
                      <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                        {a.province}
                        {a.city}
                        {a.district} {a.detail}
                      </p>
                    </button>
                  );
                })}
              </div>
            ) : (
              /* ⚠️ 网页上表单铺得开：一行放三格，不用像手机那样挤成两行。 */
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="收货人"
                    value={form.receiver}
                    onChange={(e) => setForm({ ...form, receiver: e.target.value })}
                  />
                  <Input
                    placeholder="手机号"
                    inputMode="numeric"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <RegionPicker value={form} onChange={(r) => setForm({ ...form, ...r })} />
                </div>
                <Input
                  placeholder="详细地址（含楼层，建材配送要用）"
                  value={form.detail}
                  onChange={(e) => setForm({ ...form, detail: e.target.value })}
                />
                <div className="flex gap-2">
                  <Button
                    loading={c.saveAddress.isPending}
                    onClick={async () => {
                      const saved = await c.saveAddress.mutateAsync({
                        ...form,
                        isDefault: !addresses || addresses.length === 0,
                      });
                      c.setAddressId(saved.id);
                      setShowForm(false);
                    }}
                  >
                    保存
                  </Button>
                  {addresses && addresses.length > 0 && (
                    <Button variant="ghost" onClick={() => setShowForm(false)}>
                      取消
                    </Button>
                  )}
                </div>
                {c.saveAddress.error && <ErrorBlock error={c.saveAddress.error} />}
              </div>
            )}
          </section>

          {/* ── 商品清单 ── */}
          {cart.groups.map((g) => (
            <section
              key={g.supplierOrgId}
              className="overflow-hidden rounded-xl border border-border bg-card"
            >
              <h2 className="border-b border-border bg-secondary/40 px-5 py-3 text-[13px] text-muted-foreground">
                {g.supplierName}
              </h2>

              {g.lines.map((l) => (
                <div
                  key={l.skuId}
                  className="grid grid-cols-[minmax(0,1fr)_110px_80px_110px] items-center gap-4 border-b border-border px-5 py-3.5 last:border-b-0"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Img
                      src={l.imageUrl}
                      alt={l.title}
                      className="size-12 shrink-0 rounded-lg object-cover"
                    />
                    <span className="line-clamp-2 text-[14px] leading-snug">{l.title}</span>
                  </div>
                  <span className="text-right text-[14px] text-muted-foreground">
                    <Price cents={l.unitPrice} />
                  </span>
                  <span className="text-center text-[14px] text-muted-foreground">
                    × {l.quantity}
                  </span>
                  <span className="text-right text-[14px]">
                    <Price cents={l.subtotal} />
                    {/* ⚠️ 只在这一行真的有优惠时才划 —— 相等就不显示。 */}
                    {l.originalSubtotal > l.subtotal && (
                      <span className="ml-1.5 text-[12px] text-muted-foreground line-through">
                        {fenInt(l.originalSubtotal)}
                      </span>
                    )}
                  </span>
                </div>
              ))}

              <div className="flex justify-end gap-8 border-t border-border px-5 py-2.5 text-[13px] text-muted-foreground">
                <span>
                  运费 <Price cents={g.freightAmount} />
                </span>
                <span>
                  小计 <Price cents={g.payableAmount} className="text-foreground" />
                </span>
              </div>
            </section>
          ))}

          {/* ── 备注 ── */}
          <section className="rounded-xl border border-border bg-card p-5">
            <Input
              label="订单备注"
              placeholder="如：请工作日送货、需要送到 8 楼"
              value={c.note}
              onChange={(e) => c.setNote(e.target.value)}
            />
          </section>
        </div>

        {/* ══════════ 右：要付多少（常驻） ══════════ */}
        <aside className="sticky top-24 space-y-4">
          {/* ── 优惠券 ── */}
          <section className="rounded-xl border border-border bg-card p-5">
            <p className="mb-2.5 text-[14px] font-medium">优惠券</p>
            {c.coupon ? (
              <div className="rounded-lg bg-brand-50 px-3.5 py-3">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] text-brand-700">{c.coupon.name}</p>
                    <p className="mt-0.5 text-[13px] text-brand-600">
                      减 <Price cents={c.coupon.discount} />
                    </p>
                  </div>
                  <button
                    className="shrink-0 text-[12px] text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      c.clearCoupon();
                      setCouponInput("");
                    }}
                  >
                    不用了
                  </button>
                </div>
                {/*
                  🔴 拆单时必须说清楚券用在哪一单上，
                     否则用户会以为整单都减了，看到订单金额会觉得被多扣了。
                */}
                {cart.groups.length > 1 && (
                  <p className="mt-1.5 text-[12px] text-muted-foreground">
                    用于「
                    {cart.groups.find((g) => g.supplierOrgId === c.coupon!.supplierOrgId)
                      ?.supplierName ?? "其中一"}
                    」那一单
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <Input
                    placeholder="输入券码"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    loading={c.preview.isPending}
                    disabled={!couponInput}
                    onClick={() => c.applyCoupon(couponInput)}
                  >
                    使用
                  </Button>
                </div>
                {c.couponError && (
                  <p className="mt-1.5 text-[12px] text-destructive">{c.couponError}</p>
                )}
              </>
            )}
          </section>

          {/* ── 金额明细 + 提交 ── */}
          <section className="rounded-xl border border-border bg-card p-5">
            <p className="mb-3 text-[14px] font-medium">金额明细</p>

            {/*
              🔴 「店铺优惠」和「优惠券」是**两回事**，必须分开列：
                 前者是"这家店本来就卖得比平台指导价便宜"，后者是在此之上再减。
                 合成一个数的话用户搞不清哪部分是店铺给的、哪部分是券给的。
            */}
            <dl className="space-y-2.5 text-[14px]">
              {cart.totalShopDiscount > 0 ? (
                <>
                  <div className="flex justify-between text-muted-foreground">
                    <dt>商品原价</dt>
                    <dd>
                      <Price cents={cart.totalOriginal} />
                    </dd>
                  </div>
                  <div className="flex justify-between text-brand-600">
                    <dt>店铺优惠</dt>
                    <dd>
                      −<Price cents={cart.totalShopDiscount} />
                    </dd>
                  </div>
                </>
              ) : (
                <div className="flex justify-between text-muted-foreground">
                  <dt>商品金额</dt>
                  <dd>
                    <Price cents={cart.totalGoods} />
                  </dd>
                </div>
              )}

              <div className="flex justify-between text-muted-foreground">
                <dt>运费</dt>
                <dd>
                  {cart.totalFreight > 0 ? <Price cents={cart.totalFreight} /> : "免运费"}
                </dd>
              </div>

              {c.coupon && (
                <div className="flex justify-between text-brand-600">
                  <dt>优惠券</dt>
                  <dd>
                    −<Price cents={c.coupon.discount} />
                  </dd>
                </div>
              )}
            </dl>

            <div className="mt-4 flex items-baseline justify-between border-t border-border pt-4">
              <span className="text-[14px] font-medium">实付</span>
              <Price cents={c.payable} className="text-[26px] text-brand-600" />
            </div>

            <Button
              size="lg"
              className="mt-5 w-full"
              onClick={submit}
              loading={c.createOrder.isPending}
              disabled={c.blocked}
            >
              {c.blocked ? "有商品缺货" : "提交订单"}
            </Button>

            {/*
              🔴 **提交失败的原因要贴在按钮旁边。** 以前地址没填全时这里
                 什么都不显示，用户只会反复点那个按钮。
            */}
            {c.addressError && (
              <p className="mt-2 text-[13px] text-destructive">{c.addressError}</p>
            )}

            {cart.groups.length > 1 && (
              <p className="mt-3 text-[12px] leading-relaxed text-muted-foreground">
                来自 {cart.groups.length} 个供应商，将拆成 {cart.groups.length} 张订单分别发货，
                可一起支付。
              </p>
            )}
          </section>
        </aside>
      </div>
    </DesktopShell>
  );
}
